import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router'
import {
  CartesianGrid, Cell, Line, LineChart, Pie, PieChart,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts'
import PageHeader from '../nexus/components/PageHeader'
import { useAnalyticsPeriod } from '../lib/analytics-period'
import { api, type AnalyticsRow } from '../services/api'
import ModelUsageBreakdown, { formatUsd } from '../components/ModelUsageBreakdown'

const PIE_COLORS = ['#2563eb', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6']
const TABLE_PAGE_SIZE = 10
const labels: Record<string, string> = {
  org_type: 'Type', is_active: 'Active', no_answer: 'No answer', no_answers: 'No answers',
  input_tokens: 'Input tokens', output_tokens: 'Output tokens', tokens_used: 'Tokens used',
  spend_usd: 'Spend', last_active: 'Last active', response_type: 'Response type',
  source_type: 'Source type', source_count: 'Sources', created_at: 'Created',
  source_url: 'URL', source_resource: 'Resource',
  model_usage: 'By model',
}

function heading(key: string) {
  return labels[key] || key.split('_').join(' ').replace(/^./, (letter: string) => letter.toUpperCase())
}

function value(key: string, raw: AnalyticsRow[string]) {
  if (raw == null) return '—'
  if (key === 'spend_usd') return formatUsd(Number(raw))
  if (key === 'pct') return `${raw}%`
  if (key === 'is_active') return raw ? 'Yes' : 'No'
  if (key.endsWith('_at') || key === 'day') return new Date(String(raw)).toLocaleString()
  if (typeof raw === 'number') return raw.toLocaleString()
  return String(raw)
}

function organizationHref(row: AnalyticsRow): string | null {
  if (!row.organization_id) return null
  if (row.org_type === 'IP' || row.organization_kind === 'IP') {
    return `/organizations/${encodeURIComponent(String(row.organization_id))}`
  }
  if (row.org_type === 'Email' || row.organization_kind === 'Email') {
    return `/email-organizations?organization=${encodeURIComponent(String(row.organization_id))}`
  }
  return null
}

function cellHref(column: string, row: AnalyticsRow): string | null {
  if ((column === 'email' || column === 'name') && row.user_id) {
    return `/users/${encodeURIComponent(String(row.user_id))}`
  }
  if (column === 'session' && row.session_id) {
    return `/sessions/${encodeURIComponent(String(row.session_id))}`
  }
  if (column === 'organization' || column === 'org') return organizationHref(row)
  return null
}

function CellValue({ column, row }: { column: string; row: AnalyticsRow }) {
  const raw = row[column]
  if (column === 'model_usage' && Array.isArray(raw)) return <ModelUsageBreakdown usage={raw} />
  const label = value(column, raw)
  if (column === 'spend_usd' && row.has_unpriced_models) return <span>{label} <span className="text-xs text-warning">+ unpriced</span></span>
  const href = cellHref(column, row)
  if (href) return <Link to={href} className="link link-primary link-hover">{label}</Link>
  if (column === 'source_url' && typeof raw === 'string' && /^https?:\/\//i.test(raw)) {
    return <a href={raw} target="_blank" rel="noreferrer" className="link link-primary link-hover">{label}</a>
  }
  return label
}

function paginationItems(currentPage: number, pageCount: number): Array<number | 'ellipsis'> {
  if (pageCount <= 7) return Array.from({ length: pageCount }, (_, index) => index + 1)

  const pages = [...new Set([1, currentPage - 1, currentPage, currentPage + 1, pageCount])]
    .filter((page) => page >= 1 && page <= pageCount)
    .sort((a, b) => a - b)
  const items: Array<number | 'ellipsis'> = []

  pages.forEach((page, index) => {
    if (index > 0 && page - pages[index - 1] > 1) items.push('ellipsis')
    items.push(page)
  })
  return items
}

function AnalyticsTable({ title, rows, columns, wide = false }: {
  title: string
  rows: AnalyticsRow[]
  columns: string[]
  wide?: boolean
}) {
  const [page, setPage] = useState(1)
  const pageCount = Math.max(1, Math.ceil(rows.length / TABLE_PAGE_SIZE))
  const currentPage = Math.min(page, pageCount)
  const firstRow = (currentPage - 1) * TABLE_PAGE_SIZE
  const visibleRows = rows.slice(firstRow, firstRow + TABLE_PAGE_SIZE)

  useEffect(() => setPage(1), [rows])

  return (
    <section className={`card border border-base-300 bg-base-100 ${wide ? 'xl:col-span-2' : ''}`}>
      <div className="card-body p-0">
        <h2 className="card-title px-5 pt-5">{title}</h2>
        <div className="overflow-x-auto">
          <table className="table table-sm">
            <thead><tr>{columns.map((column) => <th key={column}>{heading(column)}</th>)}</tr></thead>
            <tbody>
              {rows.length ? visibleRows.map((row, index) => (
                <tr key={`${title}-${firstRow + index}`}>
                  {columns.map((column) => <td key={column}><CellValue column={column} row={row} /></td>)}
                </tr>
              )) : <tr><td colSpan={columns.length} className="py-8 text-center text-base-content/50">No data for this period.</td></tr>}
            </tbody>
          </table>
        </div>
        {rows.length > TABLE_PAGE_SIZE && <div className="flex items-center justify-between gap-3 border-t border-base-300 px-5 py-3">
          <span className="text-sm text-base-content/60">
            {firstRow + 1}–{Math.min(firstRow + TABLE_PAGE_SIZE, rows.length)} of {rows.length}
          </span>
          <div className="join">
            <button
              type="button"
              className="btn btn-sm join-item"
              aria-label="Previous page"
              disabled={currentPage === 1}
              onClick={() => setPage((value) => Math.max(1, value - 1))}
            >‹</button>
            {paginationItems(currentPage, pageCount).map((item, index) => item === 'ellipsis' ? (
              <span key={`ellipsis-${index}`} className="btn btn-sm join-item btn-disabled" aria-hidden="true">…</span>
            ) : (
              <button
                key={item}
                type="button"
                className={`btn btn-sm join-item ${item === currentPage ? 'btn-primary' : 'btn-ghost'}`}
                aria-label={`Page ${item}`}
                aria-current={item === currentPage ? 'page' : undefined}
                onClick={() => setPage(item)}
              >{item}</button>
            ))}
            <button
              type="button"
              className="btn btn-sm join-item"
              aria-label="Next page"
              disabled={currentPage === pageCount}
              onClick={() => setPage((value) => Math.min(pageCount, value + 1))}
            >›</button>
          </div>
        </div>}
      </div>
    </section>
  )
}

export default function Analytics() {
  const { period } = useAnalyticsPeriod()
  const analytics = useQuery({
    queryKey: ['native-admin-analytics', period],
    queryFn: () => api.getAnalytics(period),
    staleTime: 30_000,
  })

  if (analytics.error) return <div className="p-6 space-y-5">
    <PageHeader title="Analytics" />
    <div className="alert alert-error">
      <span>{analytics.error instanceof Error ? analytics.error.message : 'Failed to load analytics.'}</span>
      <button type="button" className="btn btn-sm" onClick={() => analytics.refetch()}>Try again</button>
    </div>
  </div>

  if (analytics.isLoading || !analytics.data) {
    return <div className="p-8 text-center"><span className="loading loading-spinner loading-lg" /></div>
  }

  const data = analytics.data
  const queryVolume = data.daily_query_volume.map((row) => ({
    day: new Date(String(row.day)).toLocaleDateString(), queries: Number(row.queries),
  }))
  const sourceTypes = data.source_types.map((row) => ({
    name: String(row.source_type), value: Number(row.clicks),
  }))

  return (
    <div className="p-6 space-y-5">
      <PageHeader title="Analytics" subtitle="Mat-E usage, quality, spend, and source engagement." />
      <div className="grid gap-5 xl:grid-cols-2">
        <AnalyticsTable title="Per-Org Usage Summary" rows={data.organization_usage} columns={['organization', 'org_type', 'is_active', 'users', 'sessions', 'responses', 'input_tokens', 'output_tokens', 'spend_usd', 'model_usage', 'no_answer', 'external', 'internal_clicks', 'external_clicks', 'last_active']} wide />

        <section className="card border border-base-300 bg-base-100"><div className="card-body">
          <h2 className="card-title">Daily Query Volume</h2>
          {queryVolume.length ? <div className="h-72"><ResponsiveContainer width="100%" height="100%">
            <LineChart data={queryVolume} margin={{ left: 8, right: 16 }}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.25} /><XAxis dataKey="day" minTickGap={28} />
              <YAxis allowDecimals={false} /><Tooltip />
              <Line type="monotone" dataKey="queries" stroke="#2563eb" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer></div> : <p className="py-16 text-center text-base-content/50">No data for this period.</p>}
        </div></section>

        <section className="card border border-base-300 bg-base-100"><div className="card-body">
          <h2 className="card-title">Source Clicks: Internal vs External</h2>
          {sourceTypes.length ? <div className="h-72"><ResponsiveContainer width="100%" height="100%">
            <PieChart><Pie data={sourceTypes} dataKey="value" nameKey="name" outerRadius={100} label>
              {sourceTypes.map((row, index) => <Cell key={row.name} fill={PIE_COLORS[index % PIE_COLORS.length]} />)}
            </Pie><Tooltip /></PieChart>
          </ResponsiveContainer></div> : <p className="py-16 text-center text-base-content/50">No data for this period.</p>}
        </div></section>

        <AnalyticsTable title="Response Type Breakdown" rows={data.response_types} columns={['response_type', 'count', 'pct']} />
        <AnalyticsTable title="Top Users by Query Count" rows={data.top_users} columns={['email', 'name', 'organization', 'responses', 'no_answers', 'tokens_used', 'spend_usd', 'model_usage', 'last_active']} wide />
        <AnalyticsTable title="No-Answer Queries (Content Gaps)" rows={data.no_answer_queries} columns={['session', 'email', 'org', 'intent', 'source_count', 'created_at']} wide />
        <AnalyticsTable title="Spend by Organization (USD)" rows={data.spend_by_organization} columns={['organization', 'org_type', 'spend_usd', 'input_tokens', 'output_tokens', 'model_usage']} />
        <AnalyticsTable title="Spend by User (USD)" rows={data.spend_by_user} columns={['email', 'name', 'organization', 'spend_usd', 'input_tokens', 'output_tokens', 'model_usage']} />
        <AnalyticsTable title="Source Clicks by Category" rows={data.source_click_categories} columns={['category', 'clicks']} />
        <AnalyticsTable title="Source Clicks by URL (Lowest Level)" rows={data.source_click_urls} columns={['category', 'source_url', 'clicks']} />
        <AnalyticsTable title="Source Clicks by Resource (Level 2)" rows={data.source_click_resources} columns={['category', 'source_resource', 'clicks']} />
        <AnalyticsTable title="Source Clicks by Item (Level 3 — Chapter / Record)" rows={data.source_click_items} columns={['category', 'item', 'clicks']} />
      </div>
    </div>
  )
}
